import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';

import { KeyValue } from '#components/shared/KeyValue.tsx';
import { ServiceTypeName } from '#components/shared/ServiceTypeName.tsx';
import { TimeTooltip } from '#components/shared/TimeTooltip.tsx';
import { ChargingDetailsView } from '#components/tourStops/ChargingDetailsView.tsx';
import type { Documents } from '#domain/Document.ts';
import { hasStopDeviation } from '#domain/hasDeviation.ts';
import type { StopDetails } from '#domain/Stop.ts';
import { TimeDifference } from '#features/tourDetails/ui/TimeDifference.tsx';

import { useTourStopContext } from '../hooks/tourStop.context.tsx';

type TourStopDetailProps = {
    handleLoadDeviation: (stop?: StopDetails) => void;
    openStopDocuments: (stop?: StopDetails) => void;
    tourDocuments?: Documents;
};

type TourStopDetailsItemProps = {
    stop: StopDetails;
    index: number;
    handleLoadDeviation: (stop?: StopDetails) => void;
    openStopDocuments: (stop?: StopDetails) => void;
    tourDocuments?: Documents;
};

const TourStopDetailsItem = ({ stop, index, handleLoadDeviation, openStopDocuments }: TourStopDetailsItemProps) => {
    const intl = useIntl();

    return (
        <div className='timeline-element bg-lightest padding-10 rounded margin-bottom-10'>
            <div className='timeline-date margin-right-10'>
                {stop.status === 'finished' ? (
                    <div className='text-medium badge badge-success text-color-white'>{index + 1}</div>
                ) : (
                    <div className='text-medium badge bg-white border border-color-info text-color-info'>
                        {index + 1}
                    </div>
                )}
            </div>
            <div className='timeline-content width-100pct'>
                {stop.charging && <ChargingDetailsView value={stop.charging} />}
                <div className='display-flex justify-content-between align-items-start gap-10 margin-bottom-5'>
                    <div className='text-medium'>
                        {stop.type === 'execute' ? <ServiceTypeName value={stop.name} /> : stop.name}
                    </div>
                </div>
                <KeyValue
                    keyData={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.tours.stop-details.activity',
                    })}
                    valueData={
                        <FormattedMessage id={`intl-msg:ordercom-cloud-frontend.tours.stop.type.${stop.type}`} />
                    }
                />
                <KeyValue
                    keyData={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.tours.stop-details.address',
                    })}
                    valueData={`${stop.location.country} - ${stop.location.zipCode} ${stop.location.city}`}
                />
                <KeyValue keyData={''} valueData={stop.location.street} />
                <KeyValue
                    keyData={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.tours.stop-details.arrivalTime',
                    })}
                    valueData={
                        <TimeTooltip value={stop.startTarget}>
                            <div className='flex-1-1-0 text-medium'>
                                <FormattedDate
                                    value={stop.startActual ?? stop.startTarget}
                                    timeStyle={'short'}
                                    dateStyle={'short'}
                                />
                                <TimeDifference actual={stop.startActual} expected={stop.startTarget} />
                            </div>
                        </TimeTooltip>
                    }
                />
                <KeyValue
                    keyData={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.tours.stop-details.departureTime',
                    })}
                    valueData={
                        <TimeTooltip value={stop.endTarget}>
                            <div className='flex-1-1-0 text-medium'>
                                <FormattedDate
                                    value={stop.endActual ?? stop.endTarget}
                                    timeStyle={'short'}
                                    dateStyle={'short'}
                                />
                                <TimeDifference actual={stop.endActual} expected={stop.endTarget} />
                            </div>
                        </TimeTooltip>
                    }
                />
                <KeyValue
                    keyData={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.documents.tourstop.header',
                    })}
                    valueData={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.tours.stop-details.loadingConfirmation.value',
                    })}
                    isLink
                    onClick={() => openStopDocuments(stop)}
                />
                {/* {hasStopDeviation(stop) && (
                    <KeyValue
                        keyData={intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.tours.stop-details.details',
                        })}
                        valueData={
                            <span className='text-color-danger'>
                                {intl.formatMessage({
                                    id: 'intl-msg:ordercom-cloud-frontend.tours.stop-details.details.value',
                                })}
                            </span>
                        }
                    />
                )} */}

                {stop.materials.length !== 0 && (
                    <KeyValue
                        keyData={intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.tours.stop-details.loadingConfirmation',
                        })}
                        valueData={
                            <span>
                                {intl.formatMessage({
                                    id: 'intl-msg:ordercom-cloud-frontend.tours.stop-details.loadingConfirmation.value',
                                })}
                            </span>
                        }
                        valueDataAddon={
                            hasStopDeviation(stop) && (
                                <span className='text-color-danger text-size-10 margin-left-10'>
                                    {intl.formatMessage({
                                        id: 'intl-msg:ordercom-cloud-frontend.tours.stop-details.details.value',
                                    })}
                                </span>
                            )
                        }
                        isLink
                        onClick={() => handleLoadDeviation(stop)}
                    />
                )}
            </div>
        </div>
    );
};

export const TourStopDetails = ({ handleLoadDeviation, openStopDocuments, tourDocuments }: TourStopDetailProps) => {
    const { selectedTourStops: stops } = useTourStopContext();

    if (!stops) {
        return null;
    }

    return (
        <div className='timeline'>
            {stops.map((stop, index) => (
                <TourStopDetailsItem
                    index={index}
                    stop={stop}
                    key={stop.id}
                    handleLoadDeviation={handleLoadDeviation}
                    openStopDocuments={openStopDocuments}
                    tourDocuments={tourDocuments}
                />
            ))}
        </div>
    );
};
